import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "@/lib/api";
import { ExternalLink, Check, X, ArrowRight, Globe, FileCode, Globe2, Plus, Trash2, Edit2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const AVAILABLE_MARKETS = ["DE", "FR", "ES", "IT", "NL", "PL", "BE", "AT", "SE", "UK", "PT", "DK", "FI", "IE", "GR", "CZ", "RO", "HU"];

export default function Platforms() {
  const navigate = useNavigate();
  const api = useApi();
  const [platforms, setPlatforms] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    headquarters: "",
    markets: [],
    annual_revenue: ""
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [platformsRes, customPlatformsRes, countriesRes] = await Promise.all([
        api.get("/api/platforms"),
        api.get("/api/custom-platforms/"),
        api.get("/api/countries/")
      ]);
      
      // Merge base platforms with custom platforms
      const basePlatforms = platformsRes.data || [];
      const customPlatforms = customPlatformsRes.data?.platforms?.filter(p => p.is_custom) || [];
      
      // Avoid duplicates by checking IDs
      const baseIds = new Set(basePlatforms.map(p => p.id));
      const uniqueCustom = customPlatforms.filter(p => !baseIds.has(p.id));
      
      setPlatforms([...basePlatforms, ...uniqueCustom]);
      
      const countryList = countriesRes.data?.countries || [];
      setCountries(countryList.slice(0, 8));
    } catch (error) {
      console.error("Error fetching data:", error);
      setPlatforms([]);
      setCountries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlatform = async () => {
    if (!formData.name || !formData.description) {
      toast.error("Name and description are required");
      return;
    }

    try {
      await api.post("/api/custom-platforms/", {
        name: formData.name,
        description: formData.description,
        headquarters: formData.headquarters || null,
        markets: formData.markets,
        annual_revenue: formData.annual_revenue || null
      });
      toast.success("Platform created successfully");
      setShowAddDialog(false);
      setFormData({ name: "", description: "", headquarters: "", markets: [], annual_revenue: "" });
      fetchData();
    } catch (error) {
      console.error("Error creating platform:", error);
      toast.error(error.response?.data?.detail || "Failed to create platform");
    }
  };

  const handleDeletePlatform = async (platformId, platformName) => {
    if (!window.confirm(`Delete platform "${platformName}"? This cannot be undone.`)) return;

    try {
      await api.delete(`/api/custom-platforms/${platformId}`);
      toast.success("Platform deleted");
      fetchData();
    } catch (error) {
      console.error("Error deleting platform:", error);
      toast.error(error.response?.data?.detail || "Failed to delete platform");
    }
  };

  const toggleMarket = (market) => {
    setFormData(prev => ({
      ...prev,
      markets: prev.markets.includes(market)
        ? prev.markets.filter(m => m !== market)
        : [...prev.markets, market]
    }));
  };

  const getInitial = (name) => {
    return name.charAt(0).toUpperCase();
  };

  const getInitialColor = (name) => {
    const colors = {
      'A': 'bg-orange-100 text-orange-600',
      'T': 'bg-orange-100 text-orange-600', 
      'G': 'bg-blue-100 text-blue-600'
    };
    return colors[name.charAt(0).toUpperCase()] || 'bg-violet-100 text-violet-600';
  };

  if (loading) {
    return (
      <div className="content-wrapper flex items-center justify-center min-h-[50vh]">
        <div className="loading-spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="content-wrapper" data-testid="platforms-page">
      <div className="page-header compact">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Platform Profiles</h1>
            <p className="page-subtitle">
              Compliance requirements for major e-commerce platforms
            </p>
          </div>
          <Button size="sm" onClick={() => setShowAddDialog(true)} data-testid="add-platform-btn">
            <Plus className="w-4 h-4 mr-1.5" />
            Add Platform
          </Button>
        </div>
      </div>

      {/* Platforms Grid - Compact */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-6">
        {platforms.map((platform, index) => {
          const capabilities = platform.capabilities || [];
          const inHouseCount = capabilities.filter(c => c.in_house).length;
          const gapCount = capabilities.filter(c => !c.in_house).length;
          const complianceCategories = platform.compliance_categories || [];
          
          return (
            <div 
              key={platform.id}
              className={`card p-3 animate-fade-in ${platform.is_custom ? 'border-violet-200' : ''}`}
              style={{ animationDelay: `${(index % 8) * 0.05}s` }}
              data-testid={`platform-card-${platform.id}`}
            >
              <div className="flex items-start gap-2 mb-2">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${getInitialColor(platform.name)}`}>
                  <span className="text-base font-bold">{getInitial(platform.name)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h3 className="font-semibold text-sm text-gray-800 truncate">{platform.name}</h3>
                    {platform.is_custom && (
                      <Badge variant="outline" className="text-[10px] px-1">Custom</Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{platform.description}</p>
                </div>
                {platform.is_custom && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePlatform(platform.id, platform.name);
                    }}
                    data-testid={`delete-platform-${platform.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
              
              <div className="flex items-center gap-3 text-xs mb-2">
                <span className="flex items-center gap-1">
                  <Check size={12} className="text-green-500" />
                  <span className="text-gray-600">{inHouseCount}</span>
                </span>
                <span className="flex items-center gap-1">
                  <X size={12} className="text-violet-500" />
                  <span className="text-gray-600">{gapCount}</span>
                </span>
                <span className="flex items-center gap-1">
                  <Globe size={12} className="text-gray-400" />
                  <span className="text-gray-600">{platform.markets?.length || 0}</span>
                </span>
              </div>

              <div className="flex items-center gap-1 text-[10px] mb-2">
                <FileCode size={10} className="text-gray-400" />
                {platform.api_integration ? (
                  <span className="text-green-600">API</span>
                ) : (
                  <span className="text-gray-400">Manual</span>
                )}
              </div>

              {complianceCategories.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {complianceCategories.slice(0, 2).map(cat => (
                    <Badge key={cat.id} variant="secondary" className="text-[10px] px-1 py-0">
                      {cat.name}
                    </Badge>
                  ))}
                  {complianceCategories.length > 2 && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0">
                      +{complianceCategories.length - 2}
                    </Badge>
                  )}
                </div>
              )}

              <button
                onClick={() => navigate(`/platforms/${platform.id}`)}
                className="text-violet-600 text-xs font-medium flex items-center gap-1 hover:gap-1.5 transition-all"
                data-testid={`view-${platform.id}-details`}
              >
                View Details <ArrowRight size={12} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Country Compliance Overview Section */}
      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Globe2 className="w-4 h-4 text-violet-600" />
            <h2 className="text-sm font-semibold text-gray-800">Country Compliance</h2>
          </div>
          <button
            onClick={() => navigate('/countries')}
            className="text-violet-600 text-xs font-medium flex items-center gap-1 hover:gap-1.5 transition-all"
            data-testid="view-all-countries-btn"
          >
            View All <ArrowRight size={12} />
          </button>
        </div>

        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
          {countries.map((country, index) => (
            <div
              key={country.country_code}
              onClick={() => navigate(`/countries?country=${country.country_code}`)}
              className="card p-2 cursor-pointer hover:border-violet-300 transition-all text-center animate-fade-in"
              style={{ animationDelay: `${index * 0.05}s` }}
              data-testid={`country-quick-${country.country_code}`}
            >
              <span className="text-xl block">{country.flag_emoji}</span>
              <span className="text-xs font-medium text-gray-700">{country.country_code}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Add Platform Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Add New Platform</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Platform Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="E.g., Shopify, Alibaba, eBay"
                data-testid="platform-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the platform..."
                rows={2}
                data-testid="platform-description-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="headquarters">Headquarters</Label>
                <Input
                  id="headquarters"
                  value={formData.headquarters}
                  onChange={(e) => setFormData({...formData, headquarters: e.target.value})}
                  placeholder="E.g., New York, USA"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="revenue">Annual Revenue</Label>
                <Input
                  id="revenue"
                  value={formData.annual_revenue}
                  onChange={(e) => setFormData({...formData, annual_revenue: e.target.value})}
                  placeholder="E.g., $500M"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Markets</Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-gray-50">
                {AVAILABLE_MARKETS.map(market => (
                  <Badge
                    key={market}
                    variant={formData.markets.includes(market) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => toggleMarket(market)}
                  >
                    {market}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-gray-500">Click to select/deselect markets</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddPlatform} data-testid="save-platform-btn">
              Create Platform
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
